import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ServerError } from './api';
import { fa, group, toman } from './fa';

/*
 * Closing the drawer — screen ۰۴ of the design.
 *
 * The order matters and the design gets it right: expected cash is shown BEFORE
 * the count is entered. A cashier who sees the target first will, consciously
 * or not, count toward it — but hiding it until after they commit means a
 * variance arrives when it is too late to recount, and a discrepancy nobody can
 * investigate is just an accusation with a number on it.
 *
 * So the expected figure is visible, the count is typed, and both are recorded.
 * The point of a Z report is not to catch a thief; it is to find the mistake
 * while the shop is still standing there.
 */

interface Breakdown {
  method: string;
  label: string;
  amountRial: number;
}

interface Summary {
  id: string;
  openedAt: string;
  openingFloatRial: number;
  breakdown: Breakdown[];
  expectedCashRial: number;
  grossSalesRial: number;
  invoiceCount: number;
  averageBasketRial: number;
  busiestHourLabel: string | null;
  closed: boolean;
}

export function ZScreen({
  shiftId, canClose, onClosed, onBack,
}: {
  shiftId: string;
  canClose: boolean;
  onClosed: () => void;
  onBack: () => void;
}) {
  const [counted, setCounted] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const summary = useQuery({
    queryKey: ['shift-summary', shiftId],
    queryFn: () => api.get<Summary>(`/till/shifts/${shiftId}/summary`),
  });

  const close = useMutation({
    mutationFn: () =>
      api.post<{ varianceLabel: string }>(`/till/shifts/${shiftId}/close`, {
        countedCashToman: counted ?? 0,
      }),
    onSuccess: onClosed,
    onError: (e: ServerError) => setError(e.detail ?? e.message),
  });

  const s = summary.data;
  const expectedToman = s ? Math.round(s.expectedCashRial / 10) : 0;
  const value = counted ?? 0;
  const varianceRial = s ? value * 10 - s.expectedCashRial : 0;

  function press(k: string) {
    setCounted((c) => {
      let str = String(c ?? 0);
      if (k === 'del') str = str.length > 1 ? str.slice(0, -1) : '0';
      else if (k === '000') str = str === '0' ? '0' : str + '000';
      else str = str === '0' ? k : str + k;
      return Math.min(999_999_999, parseInt(str, 10) || 0);
    });
  }

  const tone =
    counted === null ? 'idle'
    : varianceRial === 0 ? 'ok'
    // Under 10,000 Toman either way is rounding and change-giving, not a
    // problem worth a red box at the end of a fourteen-hour shift.
    : Math.abs(varianceRial) <= 100_000 ? 'warn'
    : 'bad';

  const toneStyle = {
    idle: { bg: 'var(--luma-sunken)', border: 'var(--pos-rule-strong)' },
    ok: { bg: 'var(--luma-tint-success-bg)', border: 'var(--luma-tint-success-border)' },
    warn: { bg: 'var(--luma-tint-warning-bg)', border: 'var(--luma-tint-warning-border)' },
    bad: { bg: 'var(--luma-tint-danger-bg)', border: 'var(--luma-tint-danger-border)' },
  }[tone];

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <div style={{ flex: 1, minWidth: 0, padding: 18, display: 'flex', flexDirection: 'column', gap: 14, overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onBack}
            style={{
              width: 44, height: 44, borderRadius: 10, border: '1px solid var(--luma-line-strong)',
              background: 'var(--luma-card)', fontSize: 16,
            }}
          >
            →
          </button>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: 'var(--luma-placeholder)' }}>
              ۰۴ · پایان روز
            </div>
            <div style={{ fontWeight: 800, fontSize: 24 }}>بستن شیفت — گزارش Z</div>
          </div>
        </div>

        <div style={{ background: 'var(--luma-card)', border: '1px solid var(--luma-line)', borderRadius: 12, overflow: 'hidden' }}>
          <div
            style={{
              display: 'flex', padding: '10px 14px', background: 'var(--luma-sunken)',
              borderBottom: '1px solid var(--pos-rule)', fontSize: 12, fontWeight: 700,
              color: 'var(--luma-muted)',
            }}
          >
            <div style={{ flex: 1 }}>تفکیک فروش شیفت</div>
            <div>{s ? `${fa(s.invoiceCount)} فاکتور` : '…'}</div>
          </div>

          {s?.breakdown.length === 0 && (
            <div style={{ padding: '24px 14px', textAlign: 'center', color: 'var(--luma-muted)', fontSize: 13.5 }}>
              در این شیفت فروشی ثبت نشده است.
            </div>
          )}
          {s?.breakdown.map((b) => (
            <div
              key={b.method}
              style={{
                display: 'flex', justifyContent: 'space-between', padding: '12px 14px',
                borderBottom: '1px solid var(--pos-rule-faint)', fontSize: 14.5,
              }}
            >
              <span style={{ color: 'var(--pos-ink-dark)' }}>{b.label}</span>
              <span className="pos-num" style={{ fontWeight: 700 }}>{toman(b.amountRial)}</span>
            </div>
          ))}
        </div>

        <div style={{ background: 'var(--luma-card)', border: '1px solid var(--luma-line)', borderRadius: 12, padding: 14 }}>
          <div
            style={{
              display: 'flex', justifyContent: 'space-between', fontSize: 14,
              color: 'var(--luma-ink-2)', paddingBottom: 10, borderBottom: '1px solid var(--pos-rule)',
            }}
          >
            <span>نقد مورد انتظار در کشو</span>
            <span className="pos-num" style={{ fontWeight: 700, color: 'var(--luma-ink)' }}>
              {s ? toman(s.expectedCashRial) : '…'}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--luma-muted)', marginTop: 6 }}>
            موجودی اولیه {s ? toman(s.openingFloatRial) : '…'} + فروش نقدی − مرجوعی نقدی
          </div>

          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--luma-ink-2)', margin: '12px 0 8px' }}>
            نقد شمرده‌شده
          </div>
          <div
            className="pos-num"
            style={{
              height: 66, border: '1px solid var(--luma-line-strong)', borderRadius: 12,
              background: 'var(--luma-sunken)', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', padding: '0 16px',
              boxShadow: 'inset 0 0 0 3px rgba(249,124,21,.08)',
            }}
          >
            <span style={{ fontWeight: 800, fontSize: 28, color: counted === null ? 'var(--luma-placeholder)' : undefined }}>
              {counted === null ? '۰' : group(value)}
            </span>
            <span style={{ fontSize: 13, color: 'var(--luma-muted)', fontWeight: 600 }}>تومان</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, width: 320, marginTop: 12 }}>
            {['7', '8', '9', '4', '5', '6', '1', '2', '3', '000', '0', 'del'].map((k) => (
              <button
                key={k}
                onClick={() => press(k)}
                style={{
                  height: 56, borderRadius: 12, border: '1px solid var(--luma-line)',
                  background: 'var(--luma-sunken)', fontFamily: 'var(--luma-font-money)',
                  fontWeight: 700, fontSize: 20,
                }}
              >
                {k === 'del' ? '⌫' : fa(k)}
              </button>
            ))}
          </div>

          {/*
            "Same as expected" exists in the design and is worth keeping, but it
            is a shortcut for a drawer that genuinely balanced — not a way to
            skip counting. Placed away from the keypad so it is a decision.
          */}
          <button
            onClick={() => setCounted(expectedToman)}
            style={{
              marginTop: 10, height: 48, padding: '0 16px', borderRadius: 10,
              border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
              fontSize: 13.5, fontWeight: 700,
            }}
          >
            برابر مبلغ انتظار
          </button>
        </div>
      </div>

      <div
        style={{
          width: 428, flex: 'none', background: 'var(--luma-card)',
          borderInlineStart: '1px solid var(--luma-line)', padding: 18,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div style={{ borderRadius: 12, padding: 16, background: toneStyle.bg, border: `1px solid ${toneStyle.border}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--luma-ink-2)' }}>
            {counted === null ? 'هنوز شمارش نشده'
              : varianceRial === 0 ? 'کشو دقیقاً می‌خواند'
              : varianceRial > 0 ? 'اضافه در کشو' : 'کسری در کشو'}
          </div>
          <div className="pos-num" style={{ fontWeight: 800, fontSize: 30, marginTop: 2 }}>
            {counted === null ? '—' : toman(Math.abs(varianceRial))}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--pos-ink-3)', marginTop: 4, lineHeight: 1.55 }}>
            اختلاف با نام شما ثبت و در پنل به مدیر گزارش می‌شود.
          </div>
        </div>

        <div
          style={{
            border: '1px solid var(--pos-rule-strong)', borderRadius: 12, padding: '12px 14px',
            fontSize: 13, color: 'var(--luma-ink-2)', lineHeight: 1.9,
          }}
        >
          <Row k="فروش کل شیفت" v={s ? toman(s.grossSalesRial) : '…'} />
          <Row k="میانگین سبد" v={s ? toman(s.averageBasketRial) : '…'} />
          <Row k="شلوغ‌ترین ساعت" v={s?.busiestHourLabel ?? '—'} />
        </div>

        {error && (
          <div
            style={{
              background: 'var(--luma-tint-danger-bg)', border: '1px solid var(--luma-tint-danger-border)',
              borderRadius: 10, padding: '10px 14px', fontSize: 13.5,
              color: 'var(--luma-tint-danger-text)', fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}

        {/*
          Said before the button is reached, not as a rejection afterwards.
          Closing a till is a سرشیفت responsibility in most shops, and a cashier
          discovering that only when the button bounces has already counted.
        */}
        {!canClose && (
          <div
            style={{
              background: 'var(--luma-tint-warning-bg)', border: '1px solid var(--luma-tint-warning-border)',
              borderRadius: 12, padding: '12px 14px', fontSize: 12.5,
              color: 'var(--luma-accent-tint-text)', lineHeight: 1.6,
            }}
          >
            بستن شیفت دسترسی سرشیفت می‌خواهد. شمارش را وارد کنید و از سرشیفت بخواهید تأیید کند.
          </div>
        )}

        <div style={{ flex: 1 }} />

        <button
          onClick={() => close.mutate()}
          disabled={!canClose || counted === null || close.isPending}
          style={{
            height: 60, borderRadius: 12,
            background: canClose && counted !== null && !close.isPending ? 'var(--luma-accent)' : 'var(--pos-disabled)',
            color: '#FFF', fontWeight: 800, fontSize: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0 20px', boxShadow: '0 3px 10px rgba(249,124,21,.28)',
          }}
        >
          <span>{close.isPending ? 'در حال بستن…' : 'بستن شیفت و ثبت گزارش Z'}</span>
          <span style={{ fontSize: 18 }}>←</span>
        </button>

        <button
          onClick={onBack}
          style={{
            height: 48, borderRadius: 10, border: '1px solid var(--luma-line-strong)',
            background: 'var(--luma-card)', fontWeight: 700, fontSize: 14,
          }}
        >
          ادامه فروش
        </button>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span>{k}</span>
      <span className="pos-num" style={{ fontWeight: 700, color: 'var(--luma-ink)' }}>{v}</span>
    </div>
  );
}
